import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Box,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';

import type { AssemblyOrder } from '../../types/madebb.types';

interface AssemblyOrderCardProps {
  order: AssemblyOrder;
  onAssignMachine: (orderId: string) => void;
  onAdvanceStatus: (orderId: string) => void;
  onQCPass: (orderId: string) => void;
  onQCFail: (orderId: string) => void;
}

const statusMap: Record<
  AssemblyOrder['status'],
  { label: string; color: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' }
> = {
  pending: { label: 'PENDING', color: 'info' },
  in_assembly: { label: 'IN ASSEMBLY', color: 'warning' },
  quality_check: { label: 'QC', color: 'secondary' },
  pack_and_ship: { label: 'PACK & SHIP', color: 'success' },
  cancelled: { label: 'CANCELLED', color: 'error' },
};

export const AssemblyOrderCard: React.FC<AssemblyOrderCardProps> = ({
  order,
  onAssignMachine,
  onAdvanceStatus,
  onQCPass,
  onQCFail,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const statusInfo = statusMap[order.status] || { label: 'UNKNOWN', color: 'secondary' };

  return (
    <Card sx={{ minWidth: 275, boxShadow: 3, borderRadius: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography sx={{ fontWeight: 'bold' }} variant="h6" component="div">
            {order.order_number}
          </Typography>
          <Chip label={statusInfo.label} color={statusInfo.color} size="small" />
        </Box>

        <Typography variant="body2" color="text.secondary">
          {order.description}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Machine: {order.assigned_to || 'Unassigned'}
        </Typography>

        <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 'bold' }}>
          BOM Lines:
        </Typography>
        <List dense sx={{ padding: 0 }}>
          {order.bom.map((item, index) => (
            <ListItem key={index} disableGutters sx={{ padding: '0 8px' }}>
              <ListItemText primary={`${item.quantity} ${item.unit || 'pcs'} · ${item.materialName}`} />
            </ListItem>
          ))}
        </List>
      </CardContent>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, pt: 0 }}>
        <Button variant="outlined" size="small" onClick={() => onAssignMachine(order.id)} sx={{ mr: 1 }}>
          Assign Machine
        </Button>
        <Button
          id="actions-button"
          aria-controls={open ? 'actions-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          size="small"
          variant="contained"
        >
          Actions
        </Button>
        <Menu
          id="actions-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'actions-button',
          }}
        >
          <MenuItem onClick={() => { onAdvanceStatus(order.id); handleClose(); }}>
            Advance Status
          </MenuItem>
          <Divider />
          <Typography variant="caption" sx={{ pl: 2, color: 'text.secondary' }}>Quality Control</Typography>
          <MenuItem onClick={() => { onQCPass(order.id); handleClose(); }} sx={{ color: 'success.main' }}>
            Pass
          </MenuItem>
          <MenuItem onClick={() => { onQCFail(order.id); handleClose(); }} sx={{ color: 'error.main' }}>
            Fail
          </MenuItem>
        </Menu>
      </Box>
    </Card>
  );
};
